import { Head } from "@inertiajs/react"
import { Server, Copy, Check, ExternalLink } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"

export default function McpServerIndex() {
  const { t } = useTranslation()

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: t("mcp_server.breadcrumb"),
      href: "/connect_mcp",
    },
  ]

  function CopyButton({ text }: { text: string }) {
    const [copied, setCopied] = useState(false)

    const handleCopy = () => {
      navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }

    return (
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 shrink-0"
        onClick={handleCopy}
      >
        {copied ? (
          <Check className="text-primary h-4 w-4" />
        ) : (
          <Copy className="text-muted-foreground h-4 w-4" />
        )}
      </Button>
    )
  }

  const mcpConfigClaude = `{
  "mcpServers": {
    "catador-ai": {
      "url": "https://catas.sabersobrecafe.com/mcp"
    }
  }
}`

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={t("mcp_server.title")} />

      <div className="flex h-full flex-1 flex-col gap-8 p-6">
        {/* Header */}
        <div>
          <div className="mb-2 flex items-center gap-3">
            <div className="bg-primary/10 flex h-12 w-12 items-center justify-center rounded-xl">
              <Server className="text-primary h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">
                {t("mcp_server.header.title")}
              </h1>
              <p className="text-muted-foreground">
                {t("mcp_server.header.subtitle")}
              </p>
            </div>
          </div>
        </div>

        {/* Intro */}
        <Card>
          <CardContent className="p-6">
            <h2 className="mb-3 text-lg font-semibold">
              {t("mcp_server.intro.title")}
            </h2>
            <p className="text-muted-foreground mb-4">
              {t("mcp_server.intro.description")}
            </p>
            <div className="bg-muted rounded-lg border p-4">
              <p className="mb-2 text-sm font-medium">
                {t("mcp_server.intro.what_you_can_do")}
              </p>
              <ul className="text-muted-foreground space-y-1 text-sm">
                <li>• {t("mcp_server.intro.features.query_cuppings")}</li>
                <li>• {t("mcp_server.intro.features.analyze_samples")}</li>
                <li>• {t("mcp_server.intro.features.get_recommendations")}</li>
                <li>• {t("mcp_server.intro.features.generate_reports")}</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Steps */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">
            {t("mcp_server.steps.title")}
          </h2>

          {/* Step 1 */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-3 text-base">
                <span className="bg-primary text-primary-foreground flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold">
                  1
                </span>
                {t("mcp_server.steps.step1.title")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-3 text-sm">
                {t("mcp_server.steps.step1.description")}
              </p>
              <div className="bg-muted relative rounded-lg p-4 font-mono text-sm">
                <div className="absolute top-2 right-2">
                  <CopyButton text={mcpConfigClaude} />
                </div>
                <pre className="overflow-x-auto pr-10 whitespace-pre-wrap">
                  {mcpConfigClaude}
                </pre>
              </div>
            </CardContent>
          </Card>

          {/* Step 2 */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-3 text-base">
                <span className="bg-primary text-primary-foreground flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold">
                  2
                </span>
                {t("mcp_server.steps.step2.title")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4 text-sm">
                {t("mcp_server.steps.step2.description")}
              </p>
              <div className="grid gap-3 md:grid-cols-3">
                <a
                  href="https://docs.anthropic.com/en/docs/agents-and-tools/mcp"
                  target="_blank"
                >
                  <div className="hover:border-primary/50 flex cursor-pointer items-center gap-3 rounded-lg border p-4 transition-colors">
                    <div className="text-2xl">🟠</div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">
                        {t("mcp_server.steps.step2.clients.claude")}
                      </p>
                      <p className="text-muted-foreground text-xs">Anthropic</p>
                    </div>
                    <ExternalLink className="text-muted-foreground h-4 w-4" />
                  </div>
                </a>
                <a
                  href="https://platform.openai.com/docs/guides/tools"
                  target="_blank"
                >
                  <div className="hover:border-primary/50 flex cursor-pointer items-center gap-3 rounded-lg border p-4 transition-colors">
                    <div className="text-2xl">🟢</div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">
                        {t("mcp_server.steps.step2.clients.chatgpt")}
                      </p>
                      <p className="text-muted-foreground text-xs">OpenAI</p>
                    </div>
                    <ExternalLink className="text-muted-foreground h-4 w-4" />
                  </div>
                </a>
                <a href="https://ai.google.dev/docs" target="_blank">
                  <div className="hover:border-primary/50 flex cursor-pointer items-center gap-3 rounded-lg border p-4 transition-colors">
                    <div className="text-2xl">🔵</div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">
                        {t("mcp_server.steps.step2.clients.gemini")}
                      </p>
                      <p className="text-muted-foreground text-xs">Google</p>
                    </div>
                    <ExternalLink className="text-muted-foreground h-4 w-4" />
                  </div>
                </a>
              </div>
            </CardContent>
          </Card>

          {/* Step 3 */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-3 text-base">
                <span className="bg-primary text-primary-foreground flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold">
                  3
                </span>
                {t("mcp_server.steps.step3.title")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-3 text-sm">
                {t("mcp_server.steps.step3.description")}
              </p>
              <div className="space-y-2">
                {[
                  t("mcp_server.steps.step3.examples.recent_cuppings"),
                  t("mcp_server.steps.step3.examples.best_sample"),
                  t("mcp_server.steps.step3.examples.compare_samples"),
                  t("mcp_server.steps.step3.examples.unrated_samples"),
                  t("mcp_server.steps.step3.examples.activity_summary"),
                ].map((question) => (
                  <div
                    key={question}
                    className="bg-muted/50 flex items-center gap-2 rounded-lg px-4 py-2.5"
                  >
                    <span className="text-primary text-sm">→</span>
                    <span className="text-sm">{question}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  )
}
